"use client";

import { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Video, Square, Send, RefreshCw, X, Loader2, FlipHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import { useMediaPlayer } from '@/contexts/media-player-context';

type RecordingMode = 'idle' | 'recording' | 'preview' | 'uploading';
type MediaType = 'audio' | 'video';

interface RecordMessageProps {
  roomId: string;
  userId: string;   // needed for correct Storage path
  onMessageSent: (message: {
    type: 'audio' | 'video';
    url: string;
    duration: number;
    mimeType: string;
  }) => Promise<void>;
  onCancel?: () => void;
}

export function RecordMessage({
  roomId,
  userId,
  onMessageSent,
  onCancel,
}: RecordMessageProps) {
  const [mode, setMode] = useState<RecordingMode>('idle');
  const [mediaType, setMediaType] = useState<MediaType | null>(null);
  const [duration, setDuration] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Camera switching: 'user' = front, 'environment' = back/rear
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [canSwitchCamera, setCanSwitchCamera] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const liveVideoRef = useRef<HTMLVideoElement>(null);     // live camera preview during recording
  const previewVideoRef = useRef<HTMLVideoElement>(null);  // playback after recording
  const previewAudioRef = useRef<HTMLAudioElement>(null);
  const vuMeterRef = useRef<HTMLDivElement>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  // Global VU strip: cleanup fn returned by connectMicStream
  const micVuCleanupRef = useRef<(() => void) | null>(null);

  const { connectMicStream } = useMediaPlayer();

  // ── Blob URL lifecycle — create once when blob changes, revoke on cleanup ──
  useEffect(() => {
    if (!recordedBlob) { setPreviewBlobUrl(null); return; }
    const url = URL.createObjectURL(recordedBlob);
    setPreviewBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [recordedBlob]);

  // ── Check if device has multiple cameras ─────────────────────────────────
  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(devices => {
      const videoInputs = devices.filter(d => d.kind === 'videoinput');
      setCanSwitchCamera(videoInputs.length > 1);
    }).catch(() => {});
  }, []);

  // ── Timer ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (mode !== 'recording') return;
    timerRef.current = setInterval(() => {
      setDuration(prev => {
        if (prev >= 179) { stopRecording(); return 180; }
        return prev + 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── VU Meter animation (audio) ────────────────────────────────────────────
  useEffect(() => {
    if (mode !== 'recording' || !analyserRef.current || !vuMeterRef.current) return;
    const animate = () => {
      if (!analyserRef.current || !vuMeterRef.current) return;
      const data = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(data);
      const avg = data.reduce((a, b) => a + b, 0) / data.length;
      const pct = (avg / 256) * 100;
      vuMeterRef.current.querySelectorAll<HTMLElement>('[data-bar]').forEach((bar, i, all) => {
        bar.style.opacity = ((i / all.length) * 100 <= pct) ? '1' : '0.2';
      });
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [mode]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getMimeType = (type: MediaType): string => {
    if (type === 'audio') {
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
      if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
      return 'audio/mp4'; // iOS Safari fallback
    }
    // Video: WebM preferred (Chrome/Firefox), mp4 fallback for iOS Safari which doesn't support WebM
    if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')) return 'video/webm;codecs=vp9,opus';
    if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) return 'video/webm;codecs=vp8,opus';
    if (MediaRecorder.isTypeSupported('video/webm')) return 'video/webm';
    if (MediaRecorder.isTypeSupported('video/mp4')) return 'video/mp4';
    return 'video/mp4'; // iOS Safari / Android fallback
  };

  const stopStream = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  // ── Start recording ────────────────────────────────────────────────────────
  const startRecording = async (type: MediaType, facing: 'user' | 'environment' = facingMode) => {
    try {
      setError(null);
      setMediaType(type);
      setDuration(0);
      chunksRef.current = [];

      const constraints: MediaStreamConstraints =
        type === 'audio'
          ? { audio: { echoCancellation: true, noiseSuppression: true } }
          : {
              audio: { echoCancellation: true, noiseSuppression: true },
              video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: facing },
            };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Show live camera feed
      if (type === 'video' && liveVideoRef.current) {
        liveVideoRef.current.srcObject = stream;
        liveVideoRef.current.muted = true; // avoid feedback
        await liveVideoRef.current.play().catch(() => {});
      }

      // Local mini VU meter bars (in-component display)
      if (type === 'audio') {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioContextRef.current = ctx;
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyserRef.current = analyser;
        src.connect(analyser);
      }
      // Global VU strip — route mic into the global analyser chain
      micVuCleanupRef.current?.();  // clean up any prior mic connection
      micVuCleanupRef.current = connectMicStream(stream);

      const mime = getMimeType(type);
      setMimeType(mime);
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        setRecordedBlob(blob);
        setMode('preview');
        stopStream();
      };

      recorder.start();
      setMode('recording');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start recording');
      setMode('idle');
      setMediaType(null);
      stopStream();
    }
  };

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current?.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    // Disconnect mic from global VU strip
    micVuCleanupRef.current?.();
    micVuCleanupRef.current = null;
  }, []);

  // ── Switch camera mid-recording ───────────────────────────────────────────
  const handleSwitchCamera = async () => {
    if (mode !== 'recording' || mediaType !== 'video') return;
    const newFacing: 'user' | 'environment' = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacing);

    // Stop current recording/stream, start fresh with new camera
    const currentDuration = duration;
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current?.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    stopStream();
    chunksRef.current = [];
    setMode('idle'); // brief reset so useEffect cleanly re-triggers

    // Small delay then restart
    setTimeout(async () => {
      await startRecording('video', newFacing);
      // Restore timer offset so duration continues from where it was
      setDuration(currentDuration);
    }, 150);
  };

  const handleCancel = () => {
    stopRecording();
    stopStream();
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    audioContextRef.current?.close();
    audioContextRef.current = null;
    analyserRef.current = null;
    setMode('idle');
    setMediaType(null);
    setDuration(0);
    setRecordedBlob(null);
    setError(null);
    onCancel?.();
  };

  const handleReRecord = () => {
    setRecordedBlob(null); // triggers useEffect to revoke blobUrl
    setMode('idle');
  };

  // ── Upload & send ──────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!recordedBlob || !mediaType) return;
    try {
      setMode('uploading');
      const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
      // Path: chatRecordings/{userId}/{timestamp}.ext  — matches storage.rules
      const storageRef = ref(storage, `chatRecordings/${userId}/${Date.now()}.${ext}`);
      const snapshot = await uploadBytes(storageRef, recordedBlob, { contentType: mimeType });
      const url = await getDownloadURL(snapshot.ref);
      await onMessageSent({ type: mediaType, url, duration, mimeType });
      setMode('idle');
      setMediaType(null);
      setRecordedBlob(null);
      setDuration(0);
      setMimeType('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload recording');
      setMode('preview');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  // Idle — choose audio or video
  if (mode === 'idle' && !recordedBlob) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 border-t bg-secondary/30">
        <span className="text-xs text-muted-foreground flex-1">Record:</span>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-primary/20"
          onClick={() => startRecording('audio')} title="Record audio message">
          <Mic className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-primary/20"
          onClick={() => startRecording('video')} title="Record video message">
          <Video className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground"
          onClick={handleCancel} title="Cancel">
          <X className="h-4 w-4" />
        </Button>
        {error && <span className="text-xs text-destructive">{error}</span>}
      </div>
    );
  }

  // Recording
  if (mode === 'recording' && mediaType) {
    return (
      <div className="px-3 py-3 border-t bg-red-50 dark:bg-red-950/20 space-y-2">

        {/* Live video preview */}
        {mediaType === 'video' && (
          <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
            <video
              ref={liveVideoRef}
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
            />
            {/* Camera switch button */}
            {(canSwitchCamera || true /* show always, iOS/Android always have front+back */) && (
              <button
                onClick={handleSwitchCamera}
                className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                title={facingMode === 'user' ? 'Switch to back camera' : 'Switch to front camera'}
              >
                <FlipHorizontal className="h-4 w-4" />
              </button>
            )}
            {/* REC badge */}
            <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-black/60 rounded px-2 py-0.5">
              <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[11px] text-white font-medium">{formatTime(duration)}</span>
            </div>
          </div>
        )}

        {/* Audio row */}
        {mediaType === 'audio' && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-red-500 animate-pulse" />
              <span className="text-sm font-medium">Recording Audio</span>
            </div>
            <span className="text-sm font-mono text-muted-foreground">{formatTime(duration)}</span>
            <span className="text-xs text-muted-foreground ml-auto">Max 3:00</span>
          </div>
        )}

        {/* VU meter (audio) */}
        {mediaType === 'audio' && (
          <div ref={vuMeterRef} className="flex items-center gap-1 h-6 px-2 bg-card rounded border">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} data-bar className="h-4 w-1 rounded-sm bg-green-500 opacity-20 transition-opacity duration-75" />
            ))}
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="flex-1 h-9 text-xs" onClick={stopRecording}>
            <Square className="h-3.5 w-3.5 mr-1" /> Stop
          </Button>
          <Button variant="ghost" size="sm"
            className="flex-1 h-9 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleCancel}>
            <X className="h-3.5 w-3.5 mr-1" /> Cancel
          </Button>
        </div>
      </div>
    );
  }

  // Preview
  if (mode === 'preview' && recordedBlob && mediaType && previewBlobUrl) {
    return (
      <div className="px-3 py-3 border-t bg-secondary/20 space-y-2">
        <p className="text-xs font-medium text-muted-foreground">Preview</p>

        {mediaType === 'audio' ? (
          <audio ref={previewAudioRef} src={previewBlobUrl} controls className="w-full h-8 rounded bg-card" />
        ) : (
          <video ref={previewVideoRef} src={previewBlobUrl} controls playsInline autoPlay={false}
            className="w-full rounded bg-black aspect-video"
            style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }} />
        )}

        <p className="text-xs text-muted-foreground text-center">Duration: {formatTime(duration)}</p>

        {error && <p className="text-xs text-destructive text-center">{error}</p>}

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm"
            className="h-9 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            onClick={handleCancel}>
            <X className="h-3.5 w-3.5 mr-1" /> Discard
          </Button>
          <Button variant="outline" size="sm" className="flex-1 h-9 text-xs" onClick={handleReRecord}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Re-record
          </Button>
          <Button variant="default" size="sm" className="flex-1 h-9 text-xs" onClick={handleSend}>
            <Send className="h-3.5 w-3.5 mr-1" /> Send
          </Button>
        </div>
      </div>
    );
  }

  // Uploading
  if (mode === 'uploading') {
    return (
      <div className="px-3 py-3 border-t bg-secondary/20 space-y-2">
        <div className="flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <span className="text-xs font-medium">Uploading {mediaType} message...</span>
        </div>
        <div className="flex justify-center">
          <Button variant="ghost" size="sm"
            className="h-7 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            onClick={handleCancel}>
            <X className="h-3 w-3 mr-1" /> Cancel
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
